import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

import { ReadonlyDisplayBase } from './readonly-display.base';
import { secureDisplayText } from '../../secure-input-mask.util';
import { securePrefixFieldInForm, securePrefixFromForm } from '../../secure-input-prefix.util';

/**
 * Read display for a `secure-input` ("Beschermde invoer") answer: `Paspoort ••••2735`.
 *
 * The value is the masked reminder the entry keeps, never the secret (that stays in Key Vault —
 * see `secure-input-mask.util.ts`). The prefix is the answer of the field the admin picked as
 * `props.maskPrefixKeyPath`, read from the same form at render time.
 *
 * Presentational: both inputs come from the row, the prefix from the field's own form tree.
 */
@Component({
  selector: 'dd-secure-input-display',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — same reason as ValueDisplayComponent: no box inside the value span.
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `{{ text() }}`,
})
export class SecureInputDisplayComponent extends ReadonlyDisplayBase {
  /**
   * The prefix, re-read on every `valueChanges` of the prefix field's own control (seeded via
   * `startWith`). `value()` is the stored tail and stays '••••2735' when only the prefix answer
   * changes, so a computed over the inputs alone would keep printing the old label — the same
   * reason `AddressDisplayComponent` watches its root check-date control.
   */
  readonly #prefix = toSignal(
    toObservable(this.field).pipe(
      switchMap(field => {
        const control = securePrefixFieldInForm(field)?.formControl;
        const read = () => securePrefixFromForm(field);
        return control ? control.valueChanges.pipe(startWith(control.value), map(read)) : of(read());
      })
    ),
    { initialValue: null }
  );

  readonly text = computed(() => secureDisplayText(this.value(), this.#prefix()) ?? '—');
}
